/*
 * Copyright (c) Nexign, JSC, 2023.
 */
/**
 *
 *
 * @author Ilya Babanov
 * @author Evgeniy Naumov
 * @version 1.26.0
 * @since 1.23.0
 */

import omit from 'lodash/omit';

import type { IInputSearchItem } from '@/main/components/common/InputSearch/types';
import { removeEmptyValuesFromObject } from '@utils/removeEmptyValues';

import type { IAddressStringPayload, TPartialFormAddressConfiguration } from '../types/formAddressActionTypes';
import type { IFormAddressConfiguration, IFormAddressField } from '../types/formAddressTypes';
import { EFormAddressField } from '../types/formAddressTypes';

export const mapFormValuesToAddressRequest = (address: IFormAddressConfiguration) => {
    const requestParams = omit(address, [
        'addressId',
        'addressString',
        'airId',
        'apartmentOptional',
        'houseOptional',
        'planningElementOptional',
        'streetOptional',
    ]);

    return removeEmptyValuesFromObject(requestParams) as unknown as TPartialFormAddressConfiguration;
};

export function getParentElement(field: EFormAddressField, formValues: IFormAddressConfiguration) {
    const { city, planningElementOptional, planningElement, streetOptional, street, houseOptional, house, block } =
        formValues;

    const emptyCallback = () => {
        return;
    };

    const mainParent = {
        [EFormAddressField.city]: {
            id: city?.id,
        },
    };

    const getPlanningElementParent = () => {
        if (!planningElementOptional) {
            return mainParent;
        }
    };

    const getStreetParent = () => {
        if (!planningElementOptional) {
            return {
                [EFormAddressField.planningElement]: {
                    id: planningElement?.id,
                },
            };
        }

        return mainParent;
    };

    const getHouseParent = () => {
        if (!streetOptional) {
            return {
                [EFormAddressField.street]: {
                    id: street?.id,
                },
            };
        }

        return getStreetParent();
    };

    const getHouseElementParent = () => {
        if (!houseOptional) {
            return {
                ...getHouseParent(),
                [EFormAddressField.house]: {
                    id: house?.id,
                    def: house?.def,
                },
            };
        }
    };

    const getStructureParent = () => {
        if (block) {
            return {
                ...getHouseElementParent(),
                [EFormAddressField.block]: {
                    id: block.id,
                    def: block.def,
                },
            };
        }

        return {
            ...getHouseElementParent(),
        };
    };

    const formFields = {
        city: emptyCallback,
        planningElement: getPlanningElementParent,
        street: getStreetParent,
        house: getHouseParent,
        block: getHouseElementParent,
        structure: getStructureParent,
        apartment: getHouseElementParent,
        apartmentOptional: emptyCallback,
        houseOptional: emptyCallback,
        planningElementOptional: emptyCallback,
        streetOptional: emptyCallback,
    };

    return formFields[field]();
}

export function getAddressStringRequestValue(
    value: IInputSearchItem,
    field: EFormAddressField,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
): IAddressStringPayload {
    const { id, value: def, attributes } = value;

    const addressStringRequestValue: IAddressStringPayload = {
        address: {
            [field]: { id },
        },
    };

    const setAddressElement = () => {
        const formFieldValue: IFormAddressField = { id, def };
        setFieldValue(field, formFieldValue);

        return addressStringRequestValue;
    };

    const setAddressElementAttributes = () => {
        const formFieldValue: IFormAddressField = { id, def };

        if (attributes) {
            const { number, block, structure } = attributes;

            const elementAttributes = {
                [EFormAddressField.house]: number,
                [EFormAddressField.block]: block,
                [EFormAddressField.structure]: structure,
            };

            Object.entries(elementAttributes).forEach((item) => {
                const [addressElement, addressElementValue] = item as [EFormAddressField, string | undefined];

                if (addressElementValue) {
                    const newFormFieldValue = {
                        ...formFieldValue,
                        def: addressElementValue,
                    };

                    if (field === addressElement) {
                        newFormFieldValue.attributes = attributes;
                    }
                    setFieldValue(addressElement, newFormFieldValue);
                }
            });
        } else {
            setFieldValue(field, formFieldValue);
        }

        addressStringRequestValue.address = { [EFormAddressField.house]: { id } };

        return addressStringRequestValue;
    };

    const formFields = {
        [EFormAddressField.city]: setAddressElement,
        [EFormAddressField.planningElement]: setAddressElement,
        [EFormAddressField.street]: setAddressElement,
        [EFormAddressField.apartment]: setAddressElement,
        [EFormAddressField.house]: setAddressElementAttributes,
        [EFormAddressField.block]: setAddressElementAttributes,
        [EFormAddressField.structure]: setAddressElementAttributes,
        [EFormAddressField.apartmentOptional]: setAddressElement,
        [EFormAddressField.houseOptional]: setAddressElement,
        [EFormAddressField.planningElementOptional]: setAddressElement,
        [EFormAddressField.streetOptional]: setAddressElement,
    };

    return formFields[field]();
}

export function getAddressStringRequestValueAfterCheck(
    field: EFormAddressField,
    checked: boolean,
    formValues: IFormAddressConfiguration,
): IAddressStringPayload | void {
    const { city, planningElement, street, house, apartment } = formValues;

    const addressStringRequestValue: IAddressStringPayload = {
        address: {},
    };

    const emptyCallback = () => {
        return;
    };

    const getElementAddress = (field: EFormAddressField) => {
        const formField = formValues[field];
        if (formField && typeof formField === 'object' && formField.id) {
            addressStringRequestValue.address = {
                [field]: { id: formField.id },
            };
        }
    };

    const getPlanningElementParentAddress = () => {
        if (checked && planningElement) {
            getElementAddress(EFormAddressField.city);

            return addressStringRequestValue;
        }
    };

    const getStreetParentAddress = () => {
        if (checked && street) {
            const addressElement = planningElement ? EFormAddressField.planningElement : EFormAddressField.city;
            getElementAddress(addressElement);

            return addressStringRequestValue;
        }
    };

    const getHouseParentAddress = () => {
        if (checked && house) {
            if (street) {
                getElementAddress(EFormAddressField.street);
            } else if (!street && planningElement) {
                getElementAddress(EFormAddressField.planningElement);
            } else if (!street && !planningElement && city) {
                getElementAddress(EFormAddressField.city);
            }

            return addressStringRequestValue;
        }
    };

    const getApartmentParentAddress = () => {
        if (checked && apartment) {
            if (house && house.id) {
                getElementAddress(EFormAddressField.house);
            } else if (street) {
                getElementAddress(EFormAddressField.street);
            }

            return addressStringRequestValue;
        }
    };

    const formFields = {
        [EFormAddressField.city]: emptyCallback,
        [EFormAddressField.planningElement]: emptyCallback,
        [EFormAddressField.street]: emptyCallback,
        [EFormAddressField.apartment]: emptyCallback,
        [EFormAddressField.house]: emptyCallback,
        [EFormAddressField.block]: emptyCallback,
        [EFormAddressField.structure]: emptyCallback,
        [EFormAddressField.planningElementOptional]: getPlanningElementParentAddress,
        [EFormAddressField.streetOptional]: getStreetParentAddress,
        [EFormAddressField.houseOptional]: getHouseParentAddress,
        [EFormAddressField.apartmentOptional]: getApartmentParentAddress,
    };

    return formFields[field]();
}

export const trimSearchFields = (address: TPartialFormAddressConfiguration): TPartialFormAddressConfiguration => {
    const result: { [key: string]: IFormAddressField | string | null | undefined } = {};
    for (const [key, value] of Object.entries(address)) {
        const currValue = value as IFormAddressField;
        if (currValue.def) {
            result[key] = { ...currValue, def: currValue.def.trim() };
        } else {
            result[key] = currValue;
        }
    }
    return result;
};
